// FUNCTIONALITY-115 — stable checkout display number and safe referral client.
export async function reserveOrderNumber(sessionKey: string): Promise<string | null> {
  try {
    const res = await fetch("/api/store-v2/reserve-order-number", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_key: sessionKey }),
    });
    const json = (await res.json()) as { ok?: boolean; reserved_order_number?: string };
    return res.ok && json.ok !== false ? json.reserved_order_number ?? null : null;
  } catch {
    return null;
  }
}

export async function createReferralLink(
  sessionKey: string,
  orderId: string,
): Promise<{
  code: string;
  token: string;
  url: string;
  preparedMessageId: string | null;
  preparedMessageExpiresAt: string | null;
} | null> {
  try {
    const res = await fetch(`/api/store-v2/referral?order_id=${encodeURIComponent(orderId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_key: sessionKey }),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      code?: string;
      token?: string;
      url?: string;
      prepared_message_id?: string | null;
      prepared_message_expires_at?: string | null;
    };
    if (!res.ok || json.ok === false || !json.code || !json.token || !json.url) return null;
    return {
      code: json.code,
      token: json.token,
      url: json.url,
      preparedMessageId: json.prepared_message_id ?? null,
      preparedMessageExpiresAt: json.prepared_message_expires_at ?? null,
    };
  } catch {
    return null;
  }
}

export function readReferralCodeFromLaunch(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const direct = params.get("ref");
    const start = params.get("tgWebAppStartParam") ?? params.get("start_param");
    const raw = direct || (start?.startsWith("ref_") ? start.slice(4) : "");
    return /^(?:REF-[A-Z0-9]{4,20}|[a-f0-9]{32})$/i.test(raw ?? "") ? String(raw) : null;
  } catch {
    return null;
  }
}

export async function recordReferralClick(sessionKey: string, code: string): Promise<boolean> {
  try {
    const res = await fetch("/api/store-v2/referral-click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_key: sessionKey, referral_code: code }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** REFERRAL-001-R1 — startapp attribution (idempotent; flags gate server-side). */
export async function attributeReferralStartapp(
  sessionKey: string,
  startapp: string,
  telegramUserId?: string | null,
): Promise<boolean> {
  try {
    const res = await fetch("/api/store-v2/referral-attribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_key: sessionKey,
        startapp,
        telegram_user_id: telegramUserId ?? null,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export type CustomerReferralCabinet = {
  enabled: boolean;
  personal_referral_link: string | null;
  referral_code: string | null;
  invited_count: number;
  pending_rewards: number;
  approved_rewards: number;
  paid_rewards: number;
  rules_summary: string;
  status_explanations: Record<string, string>;
};

export async function fetchReferralCabinet(sessionKey: string): Promise<CustomerReferralCabinet | null> {
  try {
    const res = await fetch(
      `/api/store-v2/referral-cabinet?session_key=${encodeURIComponent(sessionKey)}`,
    );
    const json = (await res.json()) as { ok?: boolean; cabinet?: CustomerReferralCabinet };
    if (!res.ok || json.ok === false || !json.cabinet) return null;
    return json.cabinet;
  } catch {
    return null;
  }
}
